import { computed, inject, Injectable, signal } from '@angular/core';

import { ADMIN_FONTS_DATA } from '../infrastructure/admin-fonts.data';
import { FontApiResponse, FontFilter } from '../entities/font.types';

@Injectable({ providedIn: 'root' })
export class AdminFontsStore {
  private readonly dataService = inject(ADMIN_FONTS_DATA);

  readonly fonts = signal<FontApiResponse[]>([]);
  readonly loading = signal(false);
  /** List load failure: the table gives way to the error bar with a retry. */
  readonly error = signal<string | null>(null);
  readonly togglingFontId = signal<string | null>(null);
  /** Id of the font whose last publish toggle failed; the list itself stays on screen. */
  readonly toggleErrorFontId = signal<string | null>(null);
  readonly searchQuery = signal('');
  readonly fontFilter = signal<FontFilter>('all');

  readonly filteredFonts = computed(() => {
    const query = this.searchQuery().toLowerCase().trim();
    const filter = this.fontFilter();

    return this.fonts().filter(font => {
      const matchesSearch =
        !query ||
        font.fontFamily?.toLowerCase().includes(query) ||
        font.postScriptName?.toLowerCase().includes(query) ||
        font.fullName?.toLowerCase().includes(query);

      const matchesFilter =
        filter === 'all' || (filter === 'public' && font.isPublic) || (filter === 'private' && !font.isPublic);

      return matchesSearch && matchesFilter;
    });
  });

  async loadFonts(): Promise<void> {
    if (this.loading()) return;

    this.loading.set(true);
    this.error.set(null);
    this.toggleErrorFontId.set(null);

    try {
      this.fonts.set(await this.dataService.loadFonts());
    } catch {
      this.error.set('Шрифты не загрузились');
    } finally {
      this.loading.set(false);
    }
  }

  async toggleFontPublic(font: FontApiResponse): Promise<void> {
    if (this.togglingFontId()) return;

    const nextIsPublic = !font.isPublic;
    this.togglingFontId.set(font.id);
    this.toggleErrorFontId.set(null);

    try {
      await this.dataService.setFontPublic(font.id, nextIsPublic);
      // Дублирует правило сервера (FileFontService.updateFont): публичность — свойство файла.
      // Начнёт сервер переключать не все грани — эту строку надо переписать вместе с ним.
      this.fonts.update(list => list.map(f => (f.fileId === font.fileId ? { ...f, isPublic: nextIsPublic } : f)));
    } catch {
      this.toggleErrorFontId.set(font.id);
    } finally {
      this.togglingFontId.set(null);
    }
  }

  setSearch(query: string): void {
    this.searchQuery.set(query);
  }

  setFilter(filter: FontFilter): void {
    this.fontFilter.set(filter);
  }
}
